import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  MutationUpdateNetworkSearchSettingsArgs,
  Network,
  SearchNetworkSettings,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { useCachedNetwork } from '../cache/useCachedNetwork.js'

export const useUpdateNetworkSearchSettings = (options?: {
  useMutationOptions?: UseMutationOptions<
    SearchNetworkSettings,
    ClientError,
    MutationUpdateNetworkSearchSettingsArgs,
    { snapshot: Network }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const networkSnapshot = useCachedNetwork()

  return useMutation<
    SearchNetworkSettings,
    ClientError,
    MutationUpdateNetworkSearchSettingsArgs,
    { snapshot: Network }
  >(variables => client.search.updateNetworkSearchSettings(variables), {
    onMutate: async newNetworkSearchSettings => {
      await queryClient.cancelQueries(networkKey)
      const snapshot = networkSnapshot
      queryClient.setQueriesData<Network>(networkKey, old => {
        return {
          ...old,
          searchSettings: {
            ...old.searchSettings,
            ...newNetworkSearchSettings,
          },
        }
      })

      return { snapshot }
    },
    onError: (err, newNetworkSettings, context) => {
      // revert back the optimistic response
      queryClient.setQueriesData<Network>(networkKey, context.snapshot)
    },
    onSettled: () => {
      // invalidate to refetch again
      queryClient.invalidateQueries(networkKey)
    },
    ...useMutationOptions,
  })
}
